/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import { BoxesColumnsAshIcon, LockAshIcon } from '@glu/icons-react';
import { Button, TERTIARY, EXTRA_SMALL } from '@glu/buttons-react';
import { CheckboxRadio } from '@glu/form-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import locale from '@glu/locale';
import useStyles from './Column.styles';
import type { IColumnsProps, IColumnState } from '../Columns/Columns';

interface IColumnProps {
  columnState: IColumnState;
  disablePinning: IColumnsProps['disablePinning'];
  disableDrag: IColumnsProps['disableDrag'];
  onPin: NonNullable<IColumnsProps['onPin']>;
  onShow: NonNullable<IColumnsProps['onShow']>;
}

const Column = ({ columnState, disableDrag, disablePinning, onPin, onShow }: IColumnProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: columnState.colId,
    disabled: !!columnState.lockPosition || disableDrag,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const classes = useStyles();

  return (
    <div
      ref={setNodeRef}
      aria-label={columnState.headerName}
      className={classnames(classes.itemWrapper, { [classes.locked]: columnState.lockPosition })}
      style={style}
    >
      {columnState.lockPosition ? (
        <div className={classes.lockWrap}>
          <LockAshIcon />
          <p>{columnState.headerName}</p>
        </div>
      ) : (
        <CheckboxRadio
          checked={!columnState.hide}
          disableInputAPI
          labelText={columnState.headerName}
          name={columnState.colId}
          onChange={() => onShow(columnState.colId)}
        />
      )}
      <div className={classes.pinMove}>
        {disablePinning || columnState.lockPosition ? null : (
          <>
            <p>{locale.get('dataComponents.pin')}</p>
            {(columnState.pinned === 'right' || !columnState.pinned) && (
              <Button
                aria-label={locale.get('dataComponents.pinColumnLeft')}
                onClick={() => {
                  onPin(columnState.colId, 'left');
                }}
                size={EXTRA_SMALL}
                variant={TERTIARY}
              >
                {locale.get('dataComponents.left')}
              </Button>
            )}
            {!columnState.pinned ? <span className={classes.spacer}>&#124;</span> : null}
            {(columnState.pinned === 'left' || !columnState.pinned) && (
              <Button
                aria-label={locale.get('dataComponents.pinColumnRight')}
                onClick={() => {
                  onPin(columnState.colId, 'right');
                }}
                size={EXTRA_SMALL}
                variant={TERTIARY}
              >
                {locale.get('dataComponents.right')}
              </Button>
            )}
            {columnState.pinned && (
              <>
                <span className={classes.spacer}>&#124;</span>
                <Button
                  aria-label={locale.get('dataComponents.unpinColumn')}
                  onClick={() => {
                    onPin(columnState.colId, null);
                  }}
                  size={EXTRA_SMALL}
                  variant={TERTIARY}
                >
                  {locale.get('dataComponents.unpin')}
                </Button>
              </>
            )}
          </>
        )}
        {disableDrag || columnState.lockPosition ? null : (
          <div
            aria-label={locale.get('dataComponents.dragHandleLabel', columnState.headerName)}
            className={classes.dragTrigger}
            {...attributes}
            {...listeners}
          >
            <BoxesColumnsAshIcon />
          </div>
        )}
      </div>
    </div>
  );
};

export default Column;
